import BookOutlined from "@mui/icons-material/BookOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import DesignServicesOutlined from "@mui/icons-material/DesignServicesOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import StarRounded from "@mui/icons-material/StarRounded";
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Paper, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { UnifiedInsightsProductEngagementDataResponse } from "../../../interfaces/CloseTheLoop.interfaces";
import { insightCardTitleStyles, insightCardToggleStyles } from "./insightCardStyles";
import ThemePillarHeader, { themePillarStyles } from "./ThemePillarHeader";

const SUMMARY_VISUAL_NAME = "UXResearchSummaryCards";
const FINDINGS_VISUAL_NAME = "UXResearchTopCrossStudyFindings";
const ALL_UP_PERIOD = "AllUp";

type UXResearchInsightsCardProps = {
  data: UnifiedInsightsProductEngagementDataResponse[];
  product: string;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

const getSortOrder = (row: UnifiedInsightsProductEngagementDataResponse): number =>
  row.sortOrder ?? Number.MAX_SAFE_INTEGER;

const formatMetric = (value: number, label: string): string => `${value} ${label.trim().toLowerCase()}`;

const getSummaryMetricIcon = (label: string): React.ReactElement => {
  const normalizedLabel = label.trim().toLowerCase();
  if (normalizedLabel === "studies") return <BookOutlined sx={{ color: "#0F6CBD" }} />;
  if (normalizedLabel === "strong-support findings") {
    return <StarRounded sx={{ color: "#F4C430", stroke: "#C99500", strokeWidth: 1 }} />;
  }
  return <DescriptionOutlined sx={{ color: "#49627B" }} />;
};

const UXResearchInsightsCard: React.FC<UXResearchInsightsCardProps> = ({
  data,
  product,
  isFetching = false,
  isError = false,
  onRetry
}) => {
  const [expanded, setExpanded] = useState(true);
  const [collapsedPillars, setCollapsedPillars] = useState<string[]>([]);
  useEffect(() => {
    setCollapsedPillars([]);
  }, [product]);
  const allUpData = useMemo(
    () =>
      data.filter(
        ({ productName, feedbackPeriod }) =>
          Boolean(product) && productName === product && feedbackPeriod?.toLowerCase() === ALL_UP_PERIOD.toLowerCase()
      ),
    [data, product]
  );
  const summaryMetrics = useMemo(
    () =>
      allUpData
        .filter(
          ({ visualName, metricLabel, metricValue }) =>
            visualName === SUMMARY_VISUAL_NAME && Boolean(metricLabel.trim()) && metricValue !== null
        )
        .sort((left, right) => getSortOrder(left) - getSortOrder(right)),
    [allUpData]
  );
  const findings = useMemo(
    () =>
      allUpData
        .filter(
          ({ visualName, themeName, themeSummary, metricValue, metricUnit }) =>
            visualName === FINDINGS_VISUAL_NAME &&
            Boolean(themeName?.trim()) &&
            Boolean(themeSummary?.trim()) &&
            metricValue !== null &&
            metricUnit?.trim().toLowerCase() === "studies"
        )
        .sort((left, right) => getSortOrder(left) - getSortOrder(right)),
    [allUpData]
  );
  const studyCount =
    summaryMetrics.find(({ metricLabel }) => metricLabel.trim().toLowerCase() === "studies")?.metricValue ?? null;
  const findingGroups = useMemo(() => {
    const groups = new Map<string, typeof findings>();
    findings.forEach((finding) => {
      const pillar = finding.themePillar?.trim() || "Other";
      const themes = groups.get(pillar) ?? [];
      themes.push(finding);
      groups.set(pillar, themes);
    });
    return Array.from(groups, ([pillar, themes]) => ({ pillar, themes })).sort(
      (left, right) =>
        (themePillarStyles[left.pillar]?.order ?? themePillarStyles.Other.order) -
        (themePillarStyles[right.pillar]?.order ?? themePillarStyles.Other.order)
    );
  }, [findings]);
  const detailsId = "ux-research-insights-details";

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #D6DEE8",
        borderLeft: "4px solid #0F6CBD",
        borderRadius: "8px",
        bgcolor: "#FFFFFF",
        boxShadow: "0 1px 2px rgba(0,0,0,.08), 0 8px 24px rgba(15,45,75,.06)",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: "grid",
          gridTemplateColumns: { xs: "32px minmax(0, 1fr) auto", lg: "32px minmax(280px, 1fr) auto auto" },
          columnGap: 1.25,
          rowGap: 1,
          alignItems: "center",
          bgcolor: "#F6FAFE",
          background: "linear-gradient(90deg, #F7FBFF 0%, #EEF6FD 100%)"
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 30,
            height: 30,
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            bgcolor: "#DDEFFF",
            color: "#0F6CBD"
          }}
        >
          <DesignServicesOutlined sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={insightCardTitleStyles}>
            UX Research Insights
          </Typography>
          <Typography sx={{ color: "#3F5266", fontSize: 13, lineHeight: 1.5 }}>
            Cross-study findings prioritized by evidence breadth, consistency, and product impact.
          </Typography>
        </Box>
        <Box
          sx={{
            gridColumn: { xs: "2 / -1", lg: "3" },
            gridRow: { xs: 2, lg: 1 },
            display: "flex",
            flexWrap: "wrap",
            justifyContent: { xs: "flex-start", lg: "flex-end" },
            gap: 0.75
          }}
        >
          {summaryMetrics.map(({ metricKey, metricLabel, metricValue }) => (
            <Chip
              key={metricKey ?? metricLabel}
              icon={getSummaryMetricIcon(metricLabel)}
              label={formatMetric(metricValue ?? 0, metricLabel)}
              size="small"
              variant="outlined"
              sx={{
                height: 30,
                bgcolor: "#FBFCFE",
                borderColor: "#D8E2ED",
                color: "#091F3A",
                fontSize: 12,
                fontWeight: 500,
                "& .MuiChip-icon": { fontSize: 18, ml: 1, mr: -0.5 }
              }}
            />
          ))}
        </Box>
        <Button
          size="small"
          variant="contained"
          disableElevation
          aria-expanded={expanded}
          aria-controls={detailsId}
          startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setExpanded((current) => !current)}
          sx={{
            ...insightCardToggleStyles,
            gridColumn: { xs: "3", lg: "4" },
            gridRow: 1,
            justifySelf: "end",
            whiteSpace: "nowrap"
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      </Box>

      {expanded && (
        <Box id={detailsId} sx={{ p: 2, borderTop: "1px solid #D6DEE8" }}>
          {isFetching ? (
            <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography sx={{ color: "#52677D", fontSize: 13 }}>Loading UX research insights...</Typography>
            </Box>
          ) : isError ? (
            <Alert
              severity="error"
              action={
                onRetry ? (
                  <Button color="inherit" size="small" onClick={onRetry}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              Unable to load UX research insights. Please try again.
            </Alert>
          ) : findings.length === 0 ? (
            <Alert severity="info">
              {summaryMetrics.length > 0
                ? "No cross-study findings are available for the selected product."
                : "No UX research insights are available for the selected product."}
            </Alert>
          ) : (
            <Box>
              <Box
                sx={{
                  pb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2
                }}
              >
                <Typography component="h3" sx={{ color: "#091F3A", fontSize: 14, fontWeight: 800 }}>
                  Top cross-study findings
                </Typography>
                {studyCount !== null && (
                  <Typography sx={{ color: "#3F5266", fontSize: 12, textAlign: "right" }}>
                    Evidence breadth across {studyCount} studies
                  </Typography>
                )}
              </Box>
              <Stack spacing={1.25}>
                {findingGroups.map(({ pillar, themes }, groupIndex) => {
                  const isExpanded = !collapsedPillars.includes(pillar);
                  const controlsId = `ux-research-pillar-${groupIndex}`;
                  return (
                    <Box
                      key={pillar}
                      sx={{
                        overflow: "hidden",
                        border: "1px solid #E0E0E0",
                        borderRadius: 1.25,
                        bgcolor: "#FFF",
                        boxShadow: "0 2px 8px rgba(9, 31, 58, 0.05)"
                      }}
                    >
                      <ThemePillarHeader
                        pillar={pillar}
                        themeCount={themes.length}
                        studyCount={themes.reduce((total, theme) => total + (theme.metricValue ?? 0), 0)}
                        expanded={isExpanded}
                        controlsId={controlsId}
                        onToggle={() =>
                          setCollapsedPillars((current) =>
                            current.includes(pillar) ? current.filter((item) => item !== pillar) : [...current, pillar]
                          )
                        }
                      />
                      <Collapse in={isExpanded}>
                        <Box id={controlsId}>
                          {themes.map(({ id, themeName, themeSummary, metricValue, metricUnit }, index) => {
                            const evidenceBreadth = metricValue ?? 0;
                            return (
                              <Box
                                key={id}
                                sx={{
                                  px: 1.75,
                                  py: 1.5,
                                  display: "grid",
                                  gridTemplateColumns: "26px minmax(0, 1fr) auto",
                                  columnGap: 1.5,
                                  rowGap: 1,
                                  alignItems: "center",
                                  borderTop: "1px solid #E3E8EF"
                                }}
                              >
                                <Box
                                  aria-label={`Finding ${index + 1}`}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    display: "grid",
                                    placeItems: "center",
                                    borderRadius: "50%",
                                    bgcolor: "#1478D1",
                                    color: "#FFFFFF",
                                    fontSize: 12,
                                    fontWeight: 800
                                  }}
                                >
                                  {index + 1}
                                </Box>
                                <Typography sx={{ color: "#091F3A", fontSize: 14, fontWeight: 800, lineHeight: 1.4 }}>
                                  {themeName}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: "#091F3A",
                                    fontSize: 12,
                                    fontWeight: 800,
                                    textAlign: "right",
                                    whiteSpace: "nowrap"
                                  }}
                                >
                                  {formatMetric(evidenceBreadth, metricUnit ?? "studies")}
                                </Typography>
                                <Typography
                                  sx={{
                                    gridColumn: "2 / -1",
                                    minWidth: 0,
                                    overflowWrap: "anywhere",
                                    color: "#3F5266",
                                    fontSize: 14,
                                    lineHeight: 1.6
                                  }}
                                >
                                  {themeSummary}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default UXResearchInsightsCard;
